import React, { createContext, useContext, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config';

const DNContext = createContext();

export function DNProvider({ children }) {
  const now = new Date();
  const [startDate, setStartDate] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [endDate, setEndDate] = useState(new Date(now.getFullYear(), now.getMonth() + 1, 0));

  const [products, setProducts] = useState([]);
  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState(null);
  const [issuesPreviousPeriodStats, setIssuesPreviousPeriodStats] = useState(null);

  const [presetType, setPresetType] = useState(null);
  const [presetValue, setPresetValue] = useState(null);

  const fetchIssues = useCallback(async (projectId, fetchWithAuth) => {
    if (!projectId || !startDate || !endDate) return;

    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      setIssuesLoading(true);
      setIssuesError(null);

      let url = `${API_BASE_URL}/api/dwh/dn/${projectId}/issues?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

      // if (selectedShopSigns.length > 0) {
      //   selectedShopSigns.forEach(sign => {
      //     url += `&shopSigns[]=${encodeURIComponent(sign.value)}`;
      //   });
      // }

      // if (selectedCategories.length > 0) {
      //   selectedCategories.forEach(category => {
      //     url += `&categories[]=${encodeURIComponent(category.value)}`;
      //   });
      // }

      const response = await fetchWithAuth(url);

      if (response.ok) {
        const data = await response.json();
        setIssues(data.issues || []);
        setIssuesPreviousPeriodStats(data.previousPeriodStats || null);
      } else {
        setIssuesError('Failed to fetch product issues');
      }
    } catch (err) {
      setIssuesError('Error fetching product issues: ' + err.message);
    } finally {
      setIssuesLoading(false);
    }
  }, [startDate, endDate]);

  const fetchProducts = useCallback(async (projectId, fetchWithAuth, selectedShopSigns = [], selectedCategories = []) => {
    if (!projectId) return;

    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];

      let url = `${API_BASE_URL}/api/dwh/dn/${projectId}/products?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

      if (selectedShopSigns.length > 0) {
        selectedShopSigns.forEach(sign => {
          url += `&shopSigns[]=${encodeURIComponent(sign.value)}`;
        });
      }

      if (selectedCategories.length > 0) {
        selectedCategories.forEach(category => {
          url += `&categories[]=${encodeURIComponent(category.value)}`;
        });
      }

      const response = await fetchWithAuth(url);

      if (response.ok) {
        const data = await response.json();

        const productsData = data.products || data;

        const formattedProducts = productsData.map(product => ({
          value: product.productName,
          label: product.productName,
          category: product.category,
          presentShops: product.presentShops,
          notPresentShops: product.notPresentShops,
          noRefShops: product.noRefShops,
          prev_presentShops: product.prev_presentShops || 0,
          prev_notPresentShops: product.prev_notPresentShops || 0,
          prev_noRefShops: product.prev_noRefShops || 0,
          dn: (product.presentShops + product.notPresentShops) > 0
            ? product.presentShops / (product.presentShops + product.notPresentShops)
            : 0,
          prev_dn: (product.prev_presentShops + product.prev_notPresentShops) > 0
            ? product.prev_presentShops / (product.prev_presentShops + product.prev_notPresentShops)
            : 0
        }));

        setProducts(formattedProducts);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, [startDate, endDate]);

  const getFormattedDates = useCallback(() => {
    return {
      formattedStartDate: startDate.toISOString().split('T')[0],
      formattedEndDate: endDate.toISOString().split('T')[0]
    };
  }, [startDate, endDate]);

  const value = {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    products,
    issues,
    issuesLoading,
    issuesError,
    issuesPreviousPeriodStats,
    fetchProducts,
    fetchIssues,
    getFormattedDates,
    presetType,
    setPresetType,
    presetValue,
    setPresetValue
  };

  return (
    <DNContext.Provider value={value}>
      {children}
    </DNContext.Provider>
  );
}

export function useDN() {
  const context = useContext(DNContext);

  if (!context)
    throw new Error('useDN must be used within a DNProvider');

  return context;
}